import { registerEncounter } from "../slices/encounterSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetches an encounter by ID:
 * - Checks the Redux store first
 * - If not present, fetches from FHIR server and registers it
 * - Logs an error AuditEvent if fetch fails
 *
 * @param {string} encounterId
 * @param {object} fhirClient
 * @param {function} getState
 * @param {function} dispatch
 * @param {object} store - Redux store instance
 * @returns {object|null} Encounter resource or null if not found
 */
export const fetchEncounter = async (encounterId, fhirClient, store, { forceFetch = false } = {}) => {
  if (!encounterId || !fhirClient) return null;

  const cached = store.getState().encounters?.[encounterId];
  if (cached && !forceFetch) {
    //console.log("returning cached encounter");
    return cached;
  }
  try {
    const fetched = await fhirClient.request(`Encounter/${encounterId}`);
    store.dispatch(registerEncounter(fetched));
    return fetched;
  } catch (err) {
    console.error("❌ Failed to fetch encounter:", err);
    const status = err?.response?.status || err?.status || 500;
    logErrorEvent({
      errorType: "read",
      error: {
        message: err?.message || "Encounter fetch failed",
        status,
        resourceId: `Encounter/${encounterId}`
      },
      description: `Failed to retrieve Encounter/${encounterId} from FHIR server`,
      store
    });
    return { error: status };
  }
};

/**
 * Fetches an encounter by reference string (e.g., "Encounter/abc123")
 * - Validates format
 * - Extracts ID and delegates to fetchEncounter
 *
 * @param {string} ref
 * @param {object} fhirClient
 * @param {function} getState
 * @param {function} dispatch
 * @param {object} store - Redux store instance
 * @returns {object|null} Encounter resource or null if not found
 * 
 */
export const fetchEncounterByReference = async (ref, fhirClient, getState, store, options = {}) => {
  if (!ref || !ref.startsWith("Encounter/") || ref === "Encounter/undefined") {
    console.warn("⚠️ Invalid encounter reference:", ref);
    return null;
  }

  const encounterId = ref.split("/")[1];
  if (!encounterId) {
    console.warn("⚠️ Could not extract encounter ID from reference:", ref);
    return null;
  }

  return fetchEncounter(encounterId, fhirClient, getState, store, options);
};